<script setup lang="ts">
import { defineProps, withDefaults, defineEmits } from "vue";

interface BannerProps {
    title?: string;
    subtitle?: string;
    listItems?: string[]; // Optional array of strings for the list
    textContent?: string; // Alternative to listItems for simple paragraph text
    ctaText?: string; // Optional CTA button text
    ctaUrl?: string; // Optional CTA button URL
    subtext?: string; // Optional subtext below the CTA
}

const props = withDefaults(defineProps<BannerProps>(), {
    title: "",
    subtitle: "",
    listItems: () => [],
    textContent: "",
    ctaText: "Learn More",
    ctaUrl: "#",
    subtext: "",
    initiallyVisible: true,
});

const emit = defineEmits(["closed"]);

const closeBanner = (): void => {
    emit("closed");
};
</script>

<template>
    <div class="info-banner">
        <div class="banner-icon">
            <!-- Slot for customizable icon -->
            <slot name="icon">
                <!-- Default Icon (Font Awesome example) -->
                <i class="fas fa-info-circle"></i>
            </slot>
        </div>

        <div class="banner-content">
            <div class="banner-text-wrapper">
                <div class="banner-heading-line">
                    <h3 v-if="props.title" class="banner-title">{{ props.title }}</h3>
                    <span v-if="props.subtitle" class="banner-subtitle">{{ props.subtitle }}</span>
                </div>
                <slot name="content">
                    <ul v-if="props.listItems.length" class="banner-text">
                        <li v-for="(item, index) in props.listItems" :key="index">{{ item }}</li>
                    </ul>
                    <p v-else-if="props.textContent" class="banner-text">{{ props.textContent }}</p>
                </slot>
                <p v-if="props.subtext" class="banner-subtext">{{ props.subtext }}</p>
            </div>

            <a v-if="props.ctaText && props.ctaUrl" :href="props.ctaUrl" class="banner-cta" target="_blank">{{ props.ctaText }}</a>
        </div>

        <!-- Close Button -->
        <button class="banner-close" aria-label="Close banner" @click="closeBanner">×</button>
    </div>
</template>

<style scoped>
ul {
    list-style: disc;
    padding-left: 20px;
    margin-top: 5px;
    margin-bottom: 15px;
}

li {
    margin-bottom: 5px;
}

.info-banner {
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #004a98, #01c3a7);
    padding: 0 45px 0 20px;
    width: 100%;
    overflow: hidden;
    height: 100%;
}

.banner-icon {
    flex-shrink: 0;
    margin-right: 15px;
    font-size: 1.8em;
    color: #007bff;
    padding-top: 3px;
}

.banner-icon :slotted(i),
.banner-icon > i {
    color: #007bff;
}

.banner-content {
    flex-grow: 1; /* Takes up remaining horizontal space */
    display: flex; /* Make content area a flex container */
    align-items: center; /* Vertically align text wrapper and CTA */
    gap: 20px; /* Add space between text wrapper and CTA */
    min-width: 0; /* Prevent flex item overflow issues */
}

.banner-text-wrapper {
    flex-grow: 1; /* Allows text block to take available space */
    min-width: 0; /* Prevent flex item overflow issues */
    /* No specific alignment needed here, items stack naturally */
}

.banner-heading-line {
    display: flex;
    align-items: baseline; /* Align text based on baseline */
    gap: 8px; /* Space between title and subtitle */
    flex-wrap: wrap; /* Allow wrapping on very small screens */
}

.banner-title {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 1.1em;
    font-weight: bold;
}

.banner-title,
.banner-subtitle {
    color: #fff;
}

.banner-text {
    font-size: 0.9em;
    line-height: 1.5;
    color: #555;
}

.banner-cta {
    display: inline-block;
    background-color: #fff;
    color: #098574;
    padding: 10px 20px;
    text-decoration: none;
    font-weight: bold;
    border-radius: 4px;
    font-size: 0.9em;
    text-align: center;
    transition: background-color 0.2s ease;
}

.banner-cta:hover {
    background-color: #004085;
    color: #fff;
}

.banner-subtext {
    font-size: 0.8em;
    color: #fff;
    margin-bottom: 0;
    text-align: left;
}

.banner-close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 1.5em;
    color: #fff;
    cursor: pointer;
    padding: 0 5px;
    line-height: 1;
    transition: color 0.2s ease;
}

.banner-close:hover {
    color: #333;
}

@media (max-width: 600px) {
    .info-banner {
        padding: 15px;
        padding-right: 40px;
    }
    .banner-content {
        flex-direction: column;
        align-items: normal;
    }
    .banner-icon {
        margin-right: 10px;
    }
    .banner-title {
        font-size: 1em;
    }
    .banner-subtitle {
        font-size: 0.9em;
    }
    .banner-text {
        font-size: 0.85em;
    }
    .banner-cta {
        width: auto;
        padding: 8px 15px;
        font-size: 0.85em;
    }
    .banner-close {
        top: 8px;
        right: 8px;
        font-size: 1.3em;
    }
}

/* Enter Transition (Sliding In) */
.slide-right-enter-active {
    transition: all 0.5s ease-out; /* Duration and easing for slide-in */
}
.slide-right-leave-active {
    transition: all 0.3s ease-in; /* Duration and easing for slide-out */
}

.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(100%); /* Start off-screen right / End off-screen right */
    opacity: 0; /* Start faded out / End faded out */
}

.slide-right-enter-to,
.slide-right-leave-from {
    transform: translateX(0); /* End in final position / Start in final position */
    opacity: 1; /* End fully visible / Start fully visible */
}
</style>
